"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect, useId, useMemo, useState } from "react";

import { WayflyerLogo } from "@/components/nav/main-nav";
import { Input } from "@/components/ui/input";
import { SliderRoot, SliderThumb } from "@/components/ui/slider";
import {
  breakEvenRoas,
  breakEvenVolumeUplift,
  contributionProfit,
} from "@/lib/bf-toolkit/calculations";

import {
  ACQUISITION_BANDS,
  AD_BANDS,
  DEFAULTS,
  DISCOUNT_BANDS,
  GRADE_CHIP,
  GRADE_POINTS,
  RANGES,
  STRINGS,
} from "./strings";
import type { Grade } from "./strings";

interface BfReportCardProps {
  /** Request locale, used only for currency formatting. */
  locale: string;
  /** ISO currency code for Intl formatting (e.g. "USD"). */
  currency: string;
  /** Currency symbol for the input prefix (e.g. "$"). */
  currencySymbol: string;
}

// Bump the number text well above the Input default so the figures a founder
// types read clearly. twMerge resolves the font-size conflict.
const INPUT_TEXT = "text-heading-20";

const clamp = (value: number, min: number, max: number) =>
  Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : min;

/**
 * Parse a money string the way a founder might type it: strip currency symbols,
 * commas and stray characters, keep digits and a single decimal point. Returns
 * NaN for an empty/unparseable field so the UI can show a neutral prompt rather
 * than a bogus result.
 */
function parseMoney(raw: string): number {
  const digitsAndDots = raw.replace(/[^0-9.]/g, "");
  // Keep only the first decimal point; strip any subsequent ones so a typo
  // like "12.34.56" parses predictably instead of Number() truncating the
  // string at the second dot.
  const firstDot = digitsAndDots.indexOf(".");
  const cleaned =
    firstDot === -1
      ? digitsAndDots
      : digitsAndDots.slice(0, firstDot + 1) +
        digitsAndDots.slice(firstDot + 1).replace(/\./g, "");
  if (cleaned === "" || cleaned === ".") return Number.NaN;
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) ? value : Number.NaN;
}

/** Format a ROAS multiple, dropping a trailing ".0" (2.5 → "2.5×", 5 → "5×"). */
function formatRoas(n: number): string {
  const oneDp = Math.round(n * 10) / 10;
  const str = Number.isInteger(oneDp) ? String(oneDp) : oneDp.toFixed(1);
  return `${str}×`;
}

/**
 * The break-even volume uplift phrased for the discount line. Below 100% reads
 * as a plain percentage ("40% more sales"); at or above it switches to a
 * multiple ("2× the sales", "3.5× the sales").
 */
function upliftPhrase(uplift: number): string {
  if (!Number.isFinite(uplift)) return "far more sales";
  if (uplift < 100) return `${Math.round(uplift)}% more sales`;
  const mult = Math.round((uplift / 100) * 10) / 10;
  const multStr = Number.isInteger(mult) ? String(mult) : mult.toFixed(1);
  return `${multStr}× the sales`;
}

interface Dimension {
  name: string;
  grade: Grade;
  line: string;
  /** Optional badge shown beside the row name (e.g. "blended") so a caveat is
   * visible at grade-glance, not just in the disclaimer. */
  qualifier?: string;
}

/** Pick the first band whose ceiling the consumed-margin ratio falls within. */
function gradeDiscount(marginPct: number, discountPct: number): Dimension {
  const name = "Discount discipline";
  if (discountPct === 0) {
    return {
      name,
      grade: "A",
      line: "You ran no blanket discount, so your margin stayed fully intact.",
    };
  }
  // Discount at/beyond the margin wipes out contribution: no volume or ad
  // return recovers a sale that loses money on every unit.
  if (discountPct >= marginPct) {
    return {
      name,
      grade: "F",
      line: `At ${discountPct}% off on a ${marginPct}% margin, the discount wiped out your margin. Every discounted order lost money before a penny of ad spend.`,
    };
  }
  const consumed = discountPct / marginPct;
  const grade =
    DISCOUNT_BANDS.find((b) => consumed <= b.maxConsumed)?.grade ?? "D";
  // Reuse the break-even maths on a price=100 unit with the same margin.
  const uplift = breakEvenVolumeUplift(
    100,
    100 * (1 - marginPct / 100),
    discountPct
  );
  const consumedPct = Math.round(consumed * 100);
  return {
    name,
    grade,
    line: `${discountPct}% off ate ${consumedPct}% of your ${marginPct}% margin, so you needed ${upliftPhrase(uplift)} just to match a normal-price weekend.`,
  };
}

/**
 * Ad efficiency, or null when there was no ad spend to judge. `netRevenue` is
 * revenue after returns, so a returns-heavy weekend fairly drags the ROAS down.
 * The grade is blended (all revenue over all spend), flagged with a qualifier
 * so the basis is visible without reading the disclaimer.
 */
function gradeAd(
  netRevenue: number,
  adSpend: number,
  marginPct: number,
  discountPct: number
): Dimension | null {
  const name = "Ad efficiency";
  const qualifier = STRINGS.blendedQualifier;
  if (adSpend <= 0) return null;
  const beRoas = breakEvenRoas(marginPct, discountPct);
  // Margin already gone before ads: no return on ad spend can break even.
  if (!Number.isFinite(beRoas)) {
    return {
      name,
      grade: "F",
      qualifier,
      line: "Your margin was gone before ads even ran, so no level of return could have broken even.",
    };
  }
  const actualRoas = netRevenue / adSpend;
  const coverage = actualRoas / beRoas;
  const grade = AD_BANDS.find((b) => coverage >= b.minCoverage)?.grade ?? "F";
  const roasStr = formatRoas(actualRoas);
  const beStr = formatRoas(beRoas);
  const verdict =
    coverage >= 1.5
      ? "comfortably clear of it, but blended ROAS counts returning and organic sales, so your paid-only number is lower"
      : coverage >= 1.3
        ? "clear of it with a buffer, though blended ROAS flatters paid performance"
        : coverage >= 1
          ? "only just above it, and blended ROAS flatters paid, so your paid-only ads may not have paid for themselves"
          : coverage >= 0.8
            ? "just short of it, so even on a blended basis the ads ran at a small loss"
            : "below it, so even blended the ads lost money on every order";
  return {
    name,
    grade,
    qualifier,
    line: `Blended ROAS of ${roasStr} against a ${beStr} break-even: ${verdict}.`,
  };
}

/** Acquisition quality against the ~45% benchmark, with the LTV caveat. */
function gradeAcquisition(sharePct: number): Dimension {
  const name = "Acquisition quality";
  const grade =
    ACQUISITION_BANDS.find((b) => sharePct >= b.minShare)?.grade ?? "F";
  const line =
    grade === "A"
      ? `${sharePct}% of revenue came from new customers, a strong acquisition weekend. The payoff is in retention: Black Friday buyers repeat far less often, so their second purchase is where this earns out.`
      : grade === "B" || grade === "C"
        ? `${sharePct}% of revenue came from new customers, around the typical Black Friday mix. Winning them back at full price is what turns the discount into a real acquisition cost worth paying.`
        : `Only ${sharePct}% of revenue came from new customers. The discount mostly went to existing buyers who may well have bought anyway, so you paid margin for little new demand.`;
  return { name, grade, line };
}

export function BfReportCard({
  locale,
  currency,
  currencySymbol,
}: BfReportCardProps) {
  const [revenueStr, setRevenueStr] = useState(String(DEFAULTS.revenue));
  const [adSpendStr, setAdSpendStr] = useState(String(DEFAULTS.adSpend));
  const [marginPct, setMarginPct] = useState<number>(DEFAULTS.marginPct);
  const [discountPct, setDiscountPct] = useState<number>(DEFAULTS.discountPct);
  const [newCustomerSharePct, setNewCustomerSharePct] = useState<number>(
    DEFAULTS.newCustomerSharePct
  );
  const [returnsPct, setReturnsPct] = useState<number>(DEFAULTS.returnsPct);

  const revenue = parseMoney(revenueStr);
  const adSpend = parseMoney(adSpendStr);

  const formatCurrency = useMemo(() => {
    const nf = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    });
    return (value: number) => nf.format(value);
  }, [locale, currency]);

  const result = useMemo(() => {
    // Revenue and margin are the two figures nothing can be graded without.
    if (
      !Number.isFinite(revenue) ||
      revenue <= 0 ||
      !Number.isFinite(marginPct)
    ) {
      return { kind: "empty" as const };
    }
    const spend = Number.isFinite(adSpend) ? adSpend : 0;
    // Returns scale both revenue and contribution down: a returned order
    // refunds the payment but returns the goods, so it nets to ~zero margin.
    // Feeding net revenue into the shared maths keeps the headline and the
    // ROAS honest without changing the pure functions.
    const netRevenue = revenue * (1 - returnsPct / 100);
    const contribution = contributionProfit(
      netRevenue,
      marginPct,
      discountPct,
      spend
    );

    const dims: Dimension[] = [gradeDiscount(marginPct, discountPct)];
    const ad = gradeAd(netRevenue, spend, marginPct, discountPct);
    if (ad) dims.push(ad);
    dims.push(gradeAcquisition(newCustomerSharePct));

    // Overall is the rounded average of the graded dimensions' points.
    const avg =
      dims.reduce((sum, d) => sum + GRADE_POINTS[d.grade], 0) / dims.length;
    const rounded = Math.round(avg);
    const overall = (["F", "D", "C", "B", "A"] as const)[rounded] ?? "F";

    // The single biggest lever is the weakest dimension (first one on a tie).
    const focus = dims.reduce((worst, d) =>
      GRADE_POINTS[d.grade] < GRADE_POINTS[worst.grade] ? d : worst
    );

    return {
      kind: "ok" as const,
      contribution,
      dims,
      overall,
      focus: focus.name,
      spend,
      returnsPct,
    };
  }, [
    revenue,
    adSpend,
    marginPct,
    discountPct,
    newCustomerSharePct,
    returnsPct,
  ]);

  // Fire one analytics event ~800ms after the user stops adjusting, and only
  // when there's a real result. Raw money figures are intentionally not sent —
  // only the grades and the percentage inputs.
  const posthog = usePostHog();
  useEffect(() => {
    if (result.kind !== "ok") return;
    const grades = Object.fromEntries(
      result.dims.map((d) => [d.name, d.grade])
    );
    const timer = setTimeout(() => {
      posthog?.capture("bf_tool_calculated", {
        tool: "bf-report-card",
        overallGrade: result.overall,
        marginPct,
        discountPct,
        newCustomerSharePct,
        returnsPct,
        hadAdSpend: result.spend > 0,
        grades,
      });
    }, 800);
    return () => clearTimeout(timer);
    // `result` covers the overall grade, spend and per-dimension grades the
    // payload reads, so any graded outcome change re-fires this effect.
  }, [
    posthog,
    result,
    marginPct,
    discountPct,
    newCustomerSharePct,
    returnsPct,
  ]);

  return (
    <div className="group/calc border-grey-100 bg-surface relative rounded-2xl border p-5 sm:p-7">
      <MobileResultDock result={result} formatCurrency={formatCurrency} />

      <div className="grid gap-6 lg:grid-cols-2 lg:gap-10">
        {/* Inputs */}
        <div className="flex flex-col gap-5">
          <h2 className="text-heading-16 font-semibold text-[#0D0D0D]">
            {STRINGS.toolTitle}
          </h2>
          <MoneyField
            label={STRINGS.revenue}
            hint={STRINGS.revenueHint}
            value={revenueStr}
            symbol={currencySymbol}
            onChange={setRevenueStr}
          />
          <PercentField
            label={STRINGS.margin}
            hint={STRINGS.marginHint}
            value={marginPct}
            range={RANGES.percent}
            onChange={setMarginPct}
          />
          <PercentField
            label={STRINGS.discount}
            hint={STRINGS.discountHint}
            value={discountPct}
            range={RANGES.percent}
            onChange={setDiscountPct}
          />
          <MoneyField
            label={STRINGS.adSpend}
            hint={STRINGS.adSpendHint}
            value={adSpendStr}
            symbol={currencySymbol}
            onChange={setAdSpendStr}
          />
          <PercentField
            label={STRINGS.newCustomerShare}
            hint={STRINGS.newCustomerShareHint}
            value={newCustomerSharePct}
            range={RANGES.share}
            onChange={setNewCustomerSharePct}
          />
          <PercentField
            label={STRINGS.returns}
            hint={STRINGS.returnsHint}
            value={returnsPct}
            range={RANGES.returns}
            onChange={setReturnsPct}
          />
        </div>

        {/* Report card */}
        <div
          aria-live="polite"
          className="bg-pulse-50 flex min-h-[20rem] flex-col rounded-xl p-5 sm:p-6"
        >
          {result.kind === "empty" ? (
            <div className="flex flex-1 items-center">
              <p className="text-body-16 text-text-label">
                {STRINGS.resultPrompt}
              </p>
            </div>
          ) : (
            <>
              <Headline
                contribution={result.contribution}
                discountPct={discountPct}
                returnsPct={result.returnsPct}
                spend={result.spend}
                formatCurrency={formatCurrency}
              />

              <div className="bg-surface mt-5 flex items-center justify-between gap-3 rounded-xl p-4">
                <span className="text-body-14 font-semibold text-[#0D0D0D]">
                  {STRINGS.overallLabel}
                </span>
                <GradeChip grade={result.overall} large />
              </div>

              <dl className="mt-4 flex flex-col">
                {result.dims.map((d) => (
                  <div
                    key={d.name}
                    className="border-pulse-100 border-t py-3.5 first:border-t-0"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-body-14 flex items-center gap-2 font-semibold text-[#0D0D0D]">
                        {d.name}
                        {d.qualifier && (
                          <span className="text-label-12 text-grey-400 bg-grey-50 rounded-full px-2 py-0.5 font-medium lowercase">
                            {d.qualifier}
                          </span>
                        )}
                      </dt>
                      <dd>
                        <GradeChip grade={d.grade} />
                      </dd>
                    </div>
                    <p className="text-body-14 mt-1.5 leading-relaxed text-[#3f4453]">
                      {d.line}
                    </p>
                  </div>
                ))}
              </dl>

              <p className="text-body-14 border-pulse-100 mt-auto border-t pt-4 text-[#3f4453]">
                <span className="font-bold text-[#0D0D0D]">
                  {STRINGS.focusLabel}:
                </span>{" "}
                {result.focus.toLowerCase()}.
              </p>
            </>
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <p className="text-body-14 text-text-label max-w-xl italic">
          {STRINGS.disclaimer}
        </p>
        <WayflyerLogo
          aria-hidden="true"
          className="h-5 w-auto shrink-0 md:h-6 md:w-auto"
        />
      </div>
    </div>
  );
}

/** Join clause fragments as a natural list: "a", "a and b", "a, b and c". */
function joinClause(parts: string[]): string {
  if (parts.length <= 1) return parts.join("");
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

type ResultState =
  | { kind: "empty" }
  | {
      kind: "ok";
      contribution: number;
      overall: Grade;
      dims: Dimension[];
      focus: string;
      spend: number;
      returnsPct: number;
    };

/** Compact result strip shown above the mobile keyboard while a field is focused. */
function MobileResultDock({
  result,
  formatCurrency,
}: {
  result: ResultState;
  formatCurrency: (value: number) => string;
}) {
  return (
    <div
      aria-live="polite"
      aria-hidden="true"
      className="border-pulse-100 bg-pulse-50 fixed inset-x-0 bottom-0 z-40 translate-y-full border-t px-4 py-3 shadow-[0_-8px_24px_rgba(0,0,0,0.08)] transition-transform duration-200 ease-out group-focus-within/calc:translate-y-0 max-lg:block lg:hidden"
    >
      {result.kind === "empty" ? (
        <p className="text-body-14 text-text-label">{STRINGS.resultPrompt}</p>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <p className="text-body-14 min-w-0 leading-snug text-[#0D0D0D]">
            {Number.isFinite(result.contribution) ? (
              <>
                {result.contribution >= 0
                  ? "Estimated contribution "
                  : "Estimated shortfall "}
                <span
                  className={
                    result.contribution >= 0 ? "text-pulse-700" : "text-red-700"
                  }
                >
                  {formatCurrency(Math.abs(result.contribution))}
                </span>
              </>
            ) : (
              STRINGS.resultPrompt
            )}
          </p>
          <GradeChip grade={result.overall} large />
        </div>
      )}
    </div>
  );
}

function Headline({
  contribution,
  discountPct,
  returnsPct,
  spend,
  formatCurrency,
}: {
  contribution: number;
  discountPct: number;
  returnsPct: number;
  spend: number;
  formatCurrency: (value: number) => string;
}) {
  const heading =
    "text-heading-24 lg:text-heading-32 text-[#0D0D0D] leading-tight";

  if (!Number.isFinite(contribution)) {
    return (
      <p className={heading}>
        Enter a revenue and margin to grade the weekend.
      </p>
    );
  }

  const positive = contribution >= 0;
  const amount = formatCurrency(Math.abs(contribution));
  const parts = [`${discountPct}% off`];
  if (returnsPct > 0) parts.push(`${returnsPct}% returns`);
  if (spend > 0) parts.push(`${formatCurrency(spend)} in ad spend`);
  const spendClause = ` after ${joinClause(parts)}`;

  return (
    <div>
      <p className="text-label-12 text-text-label uppercase">
        {positive ? STRINGS.headlineProfit : STRINGS.headlineLoss}
      </p>
      <p className={`${heading} mt-1`}>
        Your Black Friday {positive ? "cleared about " : "came up about "}
        <span className={positive ? "text-pulse-700" : "text-red-700"}>
          {amount}
          {positive ? "" : " short"}
        </span>{" "}
        in gross contribution.
      </p>
      <p className="text-body-14 text-text-label mt-2">
        That&rsquo;s what was left{spendClause}, before fixed costs.
      </p>
    </div>
  );
}

function GradeChip({ grade, large }: { grade: Grade; large?: boolean }) {
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full font-bold ${GRADE_CHIP[grade]} ${
        large ? "text-heading-20 size-9" : "text-body-14 h-7 min-w-7 px-2"
      }`}
    >
      {grade}
    </span>
  );
}

function MoneyField({
  label,
  hint,
  value,
  symbol,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  symbol: string;
  onChange: (v: string) => void;
}) {
  const id = useId();
  return (
    <div>
      <label
        htmlFor={id}
        className="text-body-sm block font-semibold text-[#0D0D0D]"
      >
        {label}
      </label>
      <p className="text-body-14 text-text-label mt-0.5 mb-2">{hint}</p>
      <div className="relative">
        <span className="text-text-label text-heading-20 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
          {symbol}
        </span>
        <Input
          id={id}
          inputMode="decimal"
          aria-label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`h-14 pl-9 ${INPUT_TEXT}`}
        />
      </div>
    </div>
  );
}

function PercentField({
  label,
  hint,
  value,
  range,
  onChange,
}: {
  label: string;
  hint: string;
  value: number;
  range: { min: number; max: number; step: number };
  onChange: (v: number) => void;
}) {
  const id = useId();
  const { min, max, step } = range;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <label
          htmlFor={id}
          className="text-body-sm font-semibold text-[#0D0D0D]"
        >
          {label}
        </label>
        <div className="relative">
          <Input
            id={id}
            type="number"
            inputMode="numeric"
            aria-label={label}
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(clamp(Number(e.target.value), min, max))}
            className={`h-14 w-24 [appearance:textfield] pr-8 text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${INPUT_TEXT}`}
          />
          <span className="text-text-label text-heading-20 pointer-events-none absolute top-1/2 right-3 -translate-y-1/2">
            %
          </span>
        </div>
      </div>
      <p className="text-body-14 text-text-label mt-0.5 mb-2">{hint}</p>
      <SliderRoot
        aria-label={label}
        min={min}
        max={max}
        step={step}
        value={[value]}
        onValueChange={(vals) => onChange(clamp(vals[0] ?? min, min, max))}
        rangeClassName="bg-pulse-700"
        className="py-2"
      >
        <SliderThumb className="bg-pulse-700 focus-visible:ring-pulse-500 size-4 cursor-pointer rounded-full" />
      </SliderRoot>
    </div>
  );
}
